import { mayBeErrnoException } from '../util/errnoException';

export interface PreferredFileOps {
  join(...paths: string[]): string;
  isDirectory(path: string): Promise<boolean>

  // Default copy
  copyFile(src: string, dest: string): Promise<void>;

  // For fast file copy
  rename(oldPath: string, newPath: string): Promise<void>;
  mkdir(path: string): Promise<void>;
  dirname(path: string): string;
  open(path: string, flags: 'r' | 'w'): Promise<number>;
  close(fd: number): Promise<void>;
  read(fd: number, data: Uint8Array, offset: number, length: number, position: number | null): Promise<{ bytesRead: number, buffer: Uint8Array }>;
  write(fd: number, buffer: Uint8Array, offset: number, length: number): Promise<{ bytesWritten: number, buffer: Uint8Array }>;
  normalize(path: string): string;
}

export interface FilesystemApis {
  preferred: PreferredFileOps;
  canUseBlob?: boolean;
}

/**
 * The CEP version of Node (before Premiere 14.4) does not have built-in recursive option for
 * fs.mkdir(). Therefore we implement our own recursion here.
 */
export async function recursiveMkdir(fileOps: PreferredFileOps, path: string): Promise<void> {
  try {
    await fileOps.mkdir(path);
  } catch (err) {
    if (!mayBeErrnoException(err)) {
      throw err;
    }

    // in Electron, if directory already exists, it fails without setting EEXIST on err.code.
    // similarly, it fails without setting ENOENT on err.code if the parent directory does not exist.
    // so we do a string check on err.message.

    if (err.code === 'EEXIST' || err.message.includes('EEXIST: file already exists')) {
      return;
    }

    if (err.code !== 'ENOENT' && !err.message.includes('ENOENT: no such file or directory')) {
      throw err;
    }

    // ENOENT may indicate that parent directory doesn't exist.
    const above = fileOps.dirname(path);
    if (above == path) {
      throw err;
    }

    // Retry after creating parent directory first.
    await recursiveMkdir(fileOps, above);

    /*
      One of the reasons why this second mkdir() call can fail after
      successfully establishing that the parent directory exists is if the
      path length exceeds MAX_PATH on windows. It is tempting to start
      prefixing long Windows paths with \\?\ in order for the underlying OS
      API calls to support them. However, this doesn't help since the
      intermediate Node.js/libuv API layers does not simply forward the path
      string to the underlying OS API calls.
    */
    await fileOps.mkdir(path);
  }
}
